import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';

import { firstValueFrom } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { TranslateModule } from '@ngx-translate/core';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';

import type { SelectedEntity } from '@common/types/layout.type';
import { ESelectedEntityType } from '@common/types/layout.type';
import { Widgets } from '@common/enums';

import type { NormalizedBlockEntity, NormalizedSectionEntity } from '@web/types';
import { toUnsharedBlockMeta } from '@sites/entity-core';

import { LayoutFacade } from '@web/store/layout/layout.facade';
import { BlocksFacade } from '@web/store/page-structure/blocks/blocks.facade';
import { SectionsFacade } from '@web/store/page-structure/sections/sections.facade';
import { WidgetsFacade } from '@web/store/page-structure/widgets/widgets.facade';
import { ProductRestrictionFacade } from '@web/core/services/product-restriction.facade';

import { NavigationDrawerService } from '../navigation-drawer.service';
import { NavigationSectionItemComponent } from '../navigation-section-item';
import { NavigationSpecificSectionItemComponent } from '../navigation-specific-section-item';
import { NavigationZeroSectionItemComponent } from '../navigation-zero-section-item';

@Component({
    selector: 'web-navigation-block-item',
    templateUrl: './navigation-block-item.component.html',
    styleUrls: ['./navigation-block-item.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        BsDropdownModule,
        NavigationSectionItemComponent,
        NavigationSpecificSectionItemComponent,
        NavigationZeroSectionItemComponent,
        TranslateModule,
    ],
})
export class NavigationBlockItemComponent {
    public readonly entity = input.required<NormalizedBlockEntity>();
    public readonly index = input.required<number>();
    public readonly selectedEntity = input.required<SelectedEntity>();

    private readonly layoutFacade: LayoutFacade = inject(LayoutFacade);
    private readonly blocksFacade: BlocksFacade = inject(BlocksFacade);
    private readonly sectionFacade: SectionsFacade = inject(SectionsFacade);
    private readonly productRestrictionFacade: ProductRestrictionFacade = inject(ProductRestrictionFacade);
    private readonly widgetsFacade: WidgetsFacade = inject(WidgetsFacade);
    private readonly navDrawerService: NavigationDrawerService = inject(NavigationDrawerService);

    private readonly entity$ = toObservable(this.entity);
    private readonly sectionsEntities = toSignal(this.sectionFacade.sectionsEntities$, { initialValue: {} });
    private readonly widgetsEntities = toSignal(this.widgetsFacade.widgetsEntities$, { initialValue: {} });

    /** Whether this block's children are expanded — derived from the drawer service's expanded set. */
    public readonly showChildren = computed(() => this.navDrawerService.hasBlockId(this.entity().id));

    public readonly isActive = computed(() => this.selectedEntity()?.entityId === this.entity().id);
    public readonly isSpecific = computed(() => this.entity().entitySubType === 'specific');
    public readonly isFooter = computed(() => this.entity().meta.role === 'footer');
    public readonly isZero = computed(() => this.entity().entitySubType === 'zero');

    public readonly iconSrc = computed(() => {
        if (this.isSpecific()) {
            return './assets/img/websites/lpc-icon-header.svg';
        }

        return './assets/img/websites/lpc-block-icon.svg';
    });

    public readonly blockName = computed(() => {
        if (this.isSpecific()) {
            return 'header';
        }

        if (this.isFooter()) {
            return 'footer';
        }

        return 'control_block';
    });

    public readonly displayId = computed(() => this.entity().meta.displayId || this.entity().id.substring(0, 8));

    public readonly sections = computed(() => {
        const sections = this.sectionsEntities();
        return this.entity().children.map((sectionId) => sections[sectionId] as NormalizedSectionEntity);
    });

    /** Duplicating or deleting a block that holds the shop cart is not allowed. */
    public readonly isActionRestricted = computed(() => {
        const columnIds = this.sections().flatMap((section) => section?.children ?? []);

        return Object.values(this.widgetsEntities()).some(
            (widget) => widget?.name === Widgets.SHOP_CART && columnIds.includes(widget.columnId)
        );
    });

    public readonly isBlockRestricted = toSignal(
        this.entity$.pipe(switchMap((entity) => this.productRestrictionFacade.blockRestricted$(entity))),
        { initialValue: false }
    );

    public selectEntity(): void {
        if (this.navDrawerService.hasBlockId(this.entity().id)) {
            this.navDrawerService.removeBlockId(this.entity().id);
        } else {
            this.navDrawerService.addBlockId(this.entity().id);
        }

        if (this.isActive()) {
            return;
        }

        if (this.isSpecific()) {
            this.layoutFacade.setSelectedEntityWithScroll({
                entityId: this.entity().id,
                entityType: ESelectedEntityType.specificBlock,
            });
            return;
        }

        if (this.isZero()) {
            this.layoutFacade.setSelectedEntityWithScroll({
                entityId: this.entity().id,
                entityType: ESelectedEntityType.zeroBlock,
            });
            return;
        }

        this.layoutFacade.setSelectedEntityWithScroll({
            entityId: this.entity().id,
            entityType: ESelectedEntityType.block,
        });
    }

    public async removeBlock(): Promise<void> {
        if (this.isActionRestricted() || this.isBlockRestricted()) {
            return;
        }

        const block = await firstValueFrom(this.blocksFacade.blockStructureEntityById$(this.entity().id));

        if (this.isActive()) {
            this.layoutFacade.unsetEntity();
        }

        this.blocksFacade.removeBlock(block, this.index());
    }

    public async copyBlock(): Promise<void> {
        if (this.isActionRestricted() || this.isBlockRestricted()) {
            return;
        }

        const block = await firstValueFrom(this.blocksFacade.blockStructureEntityById$(this.entity().id));

        if (this.isFooter()) {
            const nextBlock = {
                ...block,
                props: { ...block.props, pin: null },
                // The copy is no longer the shared instance, but it is still a
                // footer — and `meta` has to be rebuilt rather than mutated,
                // since the spread above shares it with the entity in the store.
                meta: toUnsharedBlockMeta(block.meta),
            };

            this.blocksFacade.copyBlock(nextBlock as never, this.index());
            return;
        }

        if (this.isSpecific()) {
            const specificBlock = {
                ...block,
                props: { ...block.props, pin: null },
                meta: toUnsharedBlockMeta(block.meta),
            };

            this.blocksFacade.copyBlock(specificBlock as never, this.index() + 1);
            return;
        }

        this.blocksFacade.copyBlock(block, this.index() + 1);
    }
}
